import { MenuItem, Grid, Rating, InputLabel, FormControl, ListSubheader, TextField, Tooltip, FormHelperText } from '@mui/material';
import Select from '@mui/material/Select';
import { Theme, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { SelectDTO } from 'src/models/selectDTO.model';
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from 'src/components/Button/IconButton';
import { useTranslation } from 'react-i18next';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  MenuListProps: {
    style: {
      paddingTop: 0
    },
  },
  menuprops: {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left"
    }
  },
  getcontentanchorel: null,
  autoFocus: false,
};

function getStyles(name: string, itemName: number[], theme: Theme) {
  return {};
}

const containsText = (text: string, searchText: string) => {
  return text?.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
}

export default function ASMSelectV2(props) {
  // PROPS
  const [value, setValue] = useState<string>('');
  let liveValue = [];
  const { t } = useTranslation();
  const theme = useTheme();
  const items: SelectDTO[] = props.items || [];
  const withRating: boolean = props.withRating || false;
  const seeStar: boolean = props.seeStar || false;
  const withMultiSelect: boolean = props.withMultiSelect || false;
  const withSearch: boolean = props.withSearch || false;
  const withClose: boolean = props.withClose || false;
  const withOther: boolean = props.withOther || false;
  const grouped: boolean = props.grouped || false;
  const displayEmpty: boolean = props.displayEmpty || false;
  const placeholder = props.placeholder || '';
  const defaultValue: number[] = props.defaultValue || [];
  const isDisabled: boolean = props.isDisabled || false;
  const isRequired: boolean = props.required || false;
  const onChange: Function = props.onChange || null;
  const onChangeStar: Function = props.onChangeStar || null;
  const [open, setOpen] = useState(false);
  const errorText = props.errorText || "";
  const emptyField: boolean = props.emptyField || false;
  const fromLanguage: boolean = props.fromLanguage || false;
  const onBlur = props.onBlur || null;
  const onBlurCapture = props.onBlurCapture || null;
  const onDragLeave = props.onDragLeave || null;
  const onMouseLeave = props.onMouseLeave || (() => { });
  const [itemName, setItemName] = useState<number[]>(
    defaultValue.map(option => { return option })
  );
  // RECHERCHE
  const [searchText, setSearchText] = useState("");
  const displayedOptions = useMemo(
    () => grouped ?
      //items 
      items.map(obj => {
        return Object.assign({}, obj, {
          items: obj.items.filter(el => el.label?.toString().toLowerCase().indexOf(searchText.toLowerCase()) > -1)
        })
      }).filter(obj => obj.items.length > 0)
      : items.filter((option) => containsText(option.label, searchText)),
    [searchText]
  );

  useEffect(() => {
    setItemName(props.defaultValue ? props.defaultValue.map(option => { return option }) : []);
  }, [props.defaultValue]);

  const handleChange = (event) => {
    const prevSelected: number[] = itemName;
    setItemName(event.target.value);
    const currentValue: number[] = event.target.value;

    if (withRating) {
      if (!seeStar) {
        if (currentValue.length > 0) {
          // Initialiser la selection à 1 étoile
          if (prevSelected.length < currentValue.length) {
            var index = displayedOptions.findIndex(x => x.value === currentValue[currentValue.length - 1]);
            if (index > -1) {
              displayedOptions[index].rating = 1;
            }

            var indexItems = items.findIndex(x => x.value === displayedOptions[index].value);
            if (indexItems > -1) {
              if (items[indexItems].rating < 1)
                items[indexItems].rating = 1;
            }
          }
          // Supprimer l'étoile si c'est déselectionner
          else if (prevSelected.length > currentValue.length) {
            var valueToRemove = prevSelected.filter(e => !currentValue.find(a => e === a));
            var index = displayedOptions.findIndex(x => x.value === valueToRemove[0]);
            if (index > -1) {
              displayedOptions[index].rating = 0;
            }
            var indexItems = items.findIndex(x => x.value === valueToRemove[0]);
            if (indexItems > -1) {
              items[indexItems].rating = 0;
            }
          }
        } else {
          var index = displayedOptions.findIndex(x => x.value === prevSelected[0]);
          if (index > -1) {
            displayedOptions[index].rating = 0;
          }
          var indexItems = items.findIndex(x => x.value === displayedOptions[index].value);
          if (indexItems > -1) {
            items[indexItems].rating = 0;
          }
        }
      }
    }
    liveValue = currentValue;
    setValue(handleRenderValue(currentValue));
  };

  const renderOption = option => {
    if (withRating) {
      return (
        <MenuItem
          key={option.value}
          value={option.value}
          style={getStyles(option.label, itemName, theme)}
        >
          <Grid item xs={10}>
            {option.label}
          </Grid>
          <Grid item xs={2} style={{ paddingTop: 5, textAlign: 'right', display: option.rating > 0 ? '' : 'none' }}>
            {seeStar ?
              <Rating
                value={option.evaluation}
                key={option.value}
                size="small"
                max={4}
                readOnly
              />
              // <span>{"(" + option.evaluation + "*)"}</span>
              :
              <Rating
                value={option.rating}
                key={option.value}
                onChange={(event, newValue) => {
                  option.rating = newValue;
                  var index = items.findIndex(x => x.value === option.value);
                  if (index > -1) items[index].rating = newValue;
                  setValue(handleRenderValue(liveValue));
                  onChangeStar(option.value, newValue);
                }}
                size="small"
                max={4}
              />
            }
          </Grid>
        </MenuItem>
      )
    } else {
      if (grouped) {
        return [renderSelectGroup(option)];
      }
      return (
        <MenuItem
          key={option.value}
          value={option.value}
        >
          {(fromLanguage && option.value == 0) ? <em>{option.label}</em> : option.label}
        </MenuItem>
      )
    }
  };

  const handleRenderValue = (selected) => {
    if (selected === undefined || selected === null || selected.length === 0) {
      return "";
    }
    if (withMultiSelect) {
      const obj: SelectDTO[] = selected.map(option => {
        return items.find(x => x.value === option);
      });
      if (withRating) {
        const text = obj.sort(function (a, b) {
          return b.rating - a.rating || a.label.localeCompare(b.label);
        }).map(child => {
          if (child !== undefined) {
            if (withRating) {
              return child.label + " (" + child.rating + "*)";
            } else {
              return child.label;
            }
          }
        }).join(' / ');
        return text;
      }
      const text = obj.sort(function (a, b) {
        return a.label.localeCompare(b.label);
      }).map(child => {
        return child.label;
      }).join(' / ');

      if (obj.length > 0) {
        return text;
      } else {
        return "";
      }
    } else {
      // if (grouped) {
      //   const text = items.find(x => x.items.find(i => i.value === parseInt(selected.toString())));
      //   return text.items.find(x => x.value === parseInt(selected.toString()))?.label;
      // } 
      if (grouped) {
        const group = items.find(x => x.items.some(i => i.value === parseInt(selected.toString())));
        const text = group?.items.find(x => x.value === parseInt(selected.toString()))?.label;
        return text || "";
      }
      else {
        const text = items.find(x => x.value === parseInt(selected.toString()))?.label;
        return text;
      }
    }
  }

  const renderSelectGroup = group => {
    const items = group.items.map(c => {
      return (
        <MenuItem key={c.value} value={c.value} style={{ paddingLeft: 32 }}>
          {c.label}
        </MenuItem>
      );
    });
    return [<ListSubheader key={group.id}>{group.name}</ListSubheader>, items];
  };

  const renderSearch = props => {
    if (withSearch) {
      return (
        <ListSubheader style={{ zIndex: 5, paddingLeft: 0 }}>
          <TextField
            size="small"
            autoFocus
            variant='outlined'
            placeholder={t('search.input')}
            style={{ paddingTop: 12, paddingBottom: 12, width: withClose ? 'calc(100% - 41.5px)' : '100%' }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                e.stopPropagation();
              }
            }}
          />
          {withClose &&
            <IconButton
              variant="contained"
              color="error"
              title={t('button.close')}
              margin={5}
              marginTop="12px"
              width={36.5}
              icon={<CancelIcon style={{ fontSize: 20 }} />}
              onClick={() => {
                setSearchText("");
                setOpen(false);
              }}
            />}
        </ListSubheader>
      );
    } else {
      return (
        <MenuItem disabled value="">
          <em>{placeholder}</em>
        </MenuItem>
      )
    }
  }

  const [isHover, setIsHover] = useState(false);

  useEffect(() => {
    setValue(handleRenderValue(itemName));
  }, [])

  const renderView = () => {
    return (
      <>
        <Tooltip
          // title={value}
          title={(value == undefined || value == null) ? '' : value}
          disableHoverListener
          open={isHover}
          placement="top-start">
          <FormControl
            variant="outlined"
            style={{ width: withOther ? 'calc(100% - 45px)' : '100%' }}
            disabled={isDisabled}
            required={isRequired}
            error={!!errorText && isRequired}
          >
            <InputLabel
              sx={emptyField ? {
                color: 'red !important',
                '&.Mui-disabled': {
                  color: 'red !important',
                }
              } : {}}
              id={props.labelId}
            >{props.label}</InputLabel>
            <Select
              sx={emptyField ? {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'red !important',
                },
                '& .MuiInputLabel-root': {
                  color: 'red !important',
                },
                '& .MuiSelect-select': {
                  fontStyle: (fromLanguage && itemName != null && itemName[0] == 0) ? 'italic' : 'normal',
                }
              } : {}}
              open={open}
              labelId={props.labelId}
              label={props.label}
              error={!!errorText && isRequired}
              multiple={withMultiSelect}
              value={itemName}
              onChange={(event) => {
                handleChange(event);
                onChange(event);
              }}
              onBlur={onBlur}
              onBlurCapture={onBlurCapture}
              onDragLeave={onDragLeave}
              MenuProps={MenuProps}
              style={{ width: withOther ? 'calc(100% - 5px)' : '100%' }}
              onOpen={() => { setIsHover(false); setOpen(true); }}
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => {
                setIsHover(false)
                onMouseLeave()
              }}
              onClose={() => {
                setSearchText(""); setOpen(false);
              }}
              displayEmpty={displayEmpty}
              renderValue={(selected) => {
                return handleRenderValue(selected);
              }}
            >
              {renderSearch({})}
              {displayedOptions.map((option) => (
                renderOption(option)
              ))}
            </Select>
            {!!errorText && isRequired && <FormHelperText>{errorText}</FormHelperText>}
          </FormControl>
        </Tooltip>
      </>
    );
  }

  return (
    <>
      {renderView()}
    </>
  );
}
